import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const BADGE_STATUS_DOT_CLASS_NAME =
  "before:size-1.25 before:shrink-0 before:rounded-full before:bg-current before:content-[''] has-[>svg]:before:hidden";

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-md border border-transparent font-normal w-fit whitespace-nowrap shrink-0 gap-1.5 [&>svg]:size-3 [&>svg]:pointer-events-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring transition-colors overflow-hidden',
  {
    variants: {
      variant: {
        default:
          'bg-primary-soft text-primary-soft-foreground border-primary/20',
        secondary: 'bg-muted text-muted-foreground border-border',
        neutral: 'bg-muted text-muted-foreground border-border',
        destructive:
          'bg-destructive-soft text-destructive-soft-foreground border-destructive/20',
        success:
          'bg-success-soft text-success-soft-foreground border-success/25',
        warning:
          'bg-warning-soft text-warning-soft-foreground border-warning/30',
        info: 'bg-info-soft text-info-soft-foreground border-info/25',
        outline: 'border-border text-foreground',
        chip: 'bg-muted text-foreground',
      },
      size: {
        sm: 'h-5 px-1.5 text-xs',
        md: 'h-5 px-1.5 text-xs',
        chip: 'h-6 px-2 text-xs [&>svg]:size-3.5',
        button: 'h-7 px-2.5 text-sm [&>svg]:size-4',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
);

function Badge({
  className,
  variant,
  size,
  asChild = false,
  children,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'span';

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    >
      {children}
    </Comp>
  );
}

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

export { Badge, BADGE_STATUS_DOT_CLASS_NAME, badgeVariants };
export type { BadgeVariant };
